import { MDBModal, MDBModalFooter, MDBModalHeader, MDBIcon } from "mdbreact";
import React, {
	useState,
	useCallback,
	useEffect,
	useRef,
	useMemo,
} from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./InputImage.css";
import Button from "../../../GeneralComponents/Button";
import { useTranslation } from "react-i18next";

const getInitialCrop = (width = 1, height = 1) => {
	const safeWidth = Math.max(width || 1, 1);
	const safeHeight = Math.max(height || 1, 1);
	const cropSize = Math.min(safeWidth, safeHeight) * 0.64;
	const cropWidth = (cropSize / safeWidth) * 100;
	const cropHeight = (cropSize / safeHeight) * 100;

	return {
		unit: "%",
		aspect: 1,
		width: cropWidth,
		height: cropHeight,
		x: Math.max((100 - cropWidth) / 2, 0),
		y: Math.max((100 - cropHeight) / 2, 0),
	};
};

const getPixelCrop = (crop, image) => {
	if (!crop || !image) return null;
	if (crop.unit === "%") {
		return {
			x: (crop.x / 100) * image.width,
			y: (crop.y / 100) * image.height,
			width: (crop.width / 100) * image.width,
			height: (crop.height / 100) * image.height,
		};
	}
	return crop;
};

const InputImage = (props) => {
	const { t } = useTranslation();
	const fileRef = useRef();
	const imageRef = useRef();
	const [upImg, setUpImg] = useState();
	const [crop, setCrop] = useState();
	const [completedCrop, setCompletedCrop] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const previewCanvasRef = useRef(null);
	const imgRef = useRef(null);
	const hasPhoto = props.value !== "" && props.value !== undefined && props.value !== null;
	const avatarSize = window.matchMedia("(max-width: 576px)").matches
		? 60
		: props.width || 150;

	useEffect(() => {
		const ref = imageRef.current;
		if (!ref) return;
		if (props.value == "" || props.value == undefined) {
			return;
		}
		if (typeof props.value == "string") {
			ref.src = props.value;
			return;
		}
		var reader = new FileReader();
		reader.readAsDataURL(props.value);
		reader.onloadend = () => {
			ref.src = reader.result;
		};
	}, [props.value]);
	const imageUrl = useMemo(
		() => (typeof props.value === "string" ? props.value : undefined),
		[props.value]
	);

	const onSelectFile = (e) => {
		const [file] = e.target.files || [];
		if (file) {
			const reader = new FileReader();
			reader.addEventListener("load", () => {
				setUpImg(reader.result);
				setCrop(undefined);
				setCompletedCrop(null);
				setIsOpen(true);
			});
			reader.readAsDataURL(file);
		}
		// Selecting the same image again should still reopen the editor.
		e.target.value = "";
	};

	const onLoad = useCallback((img) => {
		imgRef.current = img;
		const renderedWidth = img.offsetWidth || img.clientWidth || img.width;
		const renderedHeight = img.offsetHeight || img.clientHeight || img.height;
		const initialCrop = getInitialCrop(renderedWidth, renderedHeight);

		setCrop(initialCrop);
		setCompletedCrop(initialCrop);
		return false;
	}, []);

	useEffect(() => {
		if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
			return;
		}
		const image = imgRef.current;
		const canvas = previewCanvasRef.current;
		const crop = getPixelCrop(completedCrop, image);
		if (!crop) return;

		const scaleX = image.naturalWidth / image.width;
		const scaleY = image.naturalHeight / image.height;
		const ctx = canvas.getContext("2d");
		const pixelRatio = window.devicePixelRatio;

		canvas.width = crop.width * pixelRatio * scaleX;
		canvas.height = crop.height * pixelRatio * scaleY;

		ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
		ctx.imageSmoothingQuality = "high";
		ctx.drawImage(
			image,
			crop.x * scaleX,
			crop.y * scaleY,
			crop.width * scaleX,
			crop.height * scaleY,
			0,
			0,
			crop.width * scaleX,
			crop.height * scaleY
		);
	}, [completedCrop]);
	function generateDownload(canvas, crop) {
		if (!crop || !canvas) {
			return;
		}
		canvas.toBlob(
			(blob) => {
				if (!blob) return;
				const roleFileName =
					props.name === "photo" ? "childPhoto.jpg" : `${props.name}.jpg`;
				let file = new File([blob], roleFileName, { type: "image/jpeg" });
				props.onChange({
					target: {
						files: [file],
						type: "file",
						id: props.name,
					},
				});
				setIsOpen(false);
			},
			"image/jpeg",
			1
		);
	}

	return (
		<div
			style={props.style}
			key={props.name}
			className={`image-upload-field ${
				props.variant === "daycare-avatar" ? "image-upload-field-daycare" : ""
			}`}
		>
			<MDBModal
				isOpen={isOpen}
				size="lg"
				centered
				toggle={() => setIsOpen(false)}
				disableBackdrop
				className="child-image-crop-modal"
			>
				<MDBModalHeader
					className="child-image-crop-header"
					titleClass="d-flex justify-content-between align-items-center w-100"
				>
					<span className="child-image-crop-title">Crop image</span>
					<button
						type="button"
						className="child-image-crop-close"
						onClick={() => setIsOpen(false)}
						aria-label="Close crop image dialog"
					>
						<MDBIcon icon="times" />
					</button>
				</MDBModalHeader>
				<div className="child-image-crop-body">
					<ReactCrop
						className="child-image-cropper"
						src={upImg}
						onImageLoaded={onLoad}
						crop={crop}
						circularCrop
						keepSelection
						onChange={(pixelCrop, percentCrop) => setCrop(percentCrop || pixelCrop)}
						onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
					/>
					<canvas
						ref={previewCanvasRef}
						style={{
							display: "none",
						}}
					/>
				</div>
				<MDBModalFooter className="child-image-crop-footer">
					<Button
						className="child-image-crop-confirm"
						onClick={() =>
							generateDownload(previewCanvasRef.current, completedCrop)
						}
					>
						Save photo
					</Button>
				</MDBModalFooter>
			</MDBModal>
			<div className="image-upload-control">
				{hasPhoto ? (
					<img
						className="image-upload-avatar"
						src={imageUrl}
						ref={imageRef}
						alt=""
						style={
							props.variant === "daycare-avatar"
								? undefined
								: { width: avatarSize, height: props.height || avatarSize }
						}
					/>
				) : (
					<div
						className="image-upload-avatar image-upload-initials"
						style={
							props.variant === "daycare-avatar"
								? undefined
								: { width: avatarSize, height: props.height || avatarSize }
						}
						aria-label={t("Avatar initials")}
					>
						{props.initials || "?"}
					</div>
				)}
				<div className="image-upload-copy">
					<p className="image-upload-label">{t(props.label)}</p>
					<p className="image-upload-help">Choose a clear image. You can adjust the crop before saving.</p>
				</div>
			</div>
			<input
				type="file"
				onChange={onSelectFile}
				accept="image/png,image/jpeg"
				ref={fileRef}
				disabled={props.disabled}
				id={props.name}
				style={{ display: "none" }}
			/>
			{props.disabled ? null : (
				<button
					type="button"
					className="image-upload-button"
					onClick={() => fileRef.current.click()}
					data-toggle="tooltip"
					data-placement="top"
					title={hasPhoto ? "Change photo" : "Choose photo"}
				>
					<span className="material-icons" aria-hidden="true">
						{hasPhoto ? "edit" : "add_a_photo"}
					</span>
					<span>{hasPhoto ? t("Change photo") : t("Choose photo")}</span>
				</button>
			)}
		</div>
	);
};

export default InputImage;
